'use client';

/**
 * BRAIN — Beast Mind brainLayer spec, VERBATIM styles:
 *   center: BEAST CORE title, 260px breathing orb + spin rings + pulse ring,
 *           glowing counts row, hint pill
 *   left  380px: RECENT SESSIONS panel (live gateway sessions) + ISSUES (doctor)
 *   right 400px: .SOMA ANATOMY — MUSCLES chip cloud (heat-as-glow),
 *                PROTOCOLS behavioral rules w/ heat dots
 * Data: same /api/gw/soma/* + /api/gw/sessions as classic. Preloads + MAPs
 * panels (v6 Beast Mind) wired to /api/gw/soma/preloads + /soma/maps — the
 * endpoints v3 was waiting for now exist. Honest empty state when none.
 *
 * SESSIONS surface (live session table) lives at the bottom of this file.
 */
import { useEffect, useRef, useState } from 'react';
import { Glass, mono, StatusChip } from '@/future/primitives';

const GW = '/api/gw';

type Heated = { name: string; heat: number; status?: string };
type Doctor = { healthy?: boolean; checks?: { name: string; status: string; detail?: string }[] };

const heatOf = (h: number) => h >= 70 ? { c: '#ffc36b', glow: 'rgba(255,195,107,.55)', k: 'hot' }
  : h >= 30 ? { c: '#9ef0ff', glow: 'rgba(0,240,255,.4)', k: 'warm' }
  : { c: '#56788c', glow: 'transparent', k: 'cold' };

const panel: React.CSSProperties = {
  position: 'relative', borderRadius: 14, border: '1px solid rgba(125,232,255,.22)',
  background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.45) 55%)',
  padding: 16, overflow: 'hidden',
};
const Scan = () => <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.05) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' }} />;

export function BrainSurface() {
  const [protocols, setProtocols] = useState<Heated[] | null>(null);
  const [muscles, setMuscles] = useState<Heated[] | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [preloads, setPreloads] = useState<any>(null);
  const [maps, setMaps] = useState<any[] | null>(null);

  useEffect(() => {
    let live = true;
    const load = () => {
      fetch(`${GW}/soma/protocols`).then(r => r.json()).then(d => live && setProtocols(d.protocols || [])).catch(() => live && setProtocols([]));
      fetch(`${GW}/soma/muscles`).then(r => r.json()).then(d => live && setMuscles(d.muscles || [])).catch(() => live && setMuscles([]));
      fetch(`${GW}/soma/doctor`).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(d => live && setDoctor(d)).catch(() => live && setDoctor({ checks: [{ name: 'Doctor endpoint', status: 'warn', detail: 'unreachable — restart gateway' }] }));
      fetch(`${GW}/sessions`).then(r => r.json()).then(d => live && setSessions(d.sessions || [])).catch(() => {});
      fetch(`${GW}/soma/preloads`).then(r => r.json()).then(d => live && setPreloads(d)).catch(() => {});
      fetch(`${GW}/soma/maps`).then(r => r.json()).then(d => live && setMaps(d.maps || [])).catch(() => live && setMaps([]));
    };
    load();
    const t = setInterval(load, 120_000);
    return () => { live = false; clearInterval(t); };
  }, []);

  if (protocols === null || muscles === null)
    return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING NEURAL STATE…</div>;

  const hotMuscles = [...muscles].sort((a, b) => b.heat - a.heat).slice(0, 24);
  const hotProtocols = [...protocols].sort((a, b) => b.heat - a.heat).slice(0, 6);
  const issues = (doctor?.checks || []).filter(c => c.status !== 'ok').slice(0, 4);
  const stats = [
    { l: 'SESSIONS', v: String(sessions.length) },
    { l: 'MUSCLES', v: String(muscles.length) },
    { l: 'PROTOCOLS', v: String(protocols.length) },
    { l: 'WORKING', v: String(sessions.filter(s => s.state === 'working').length) },
  ];

  return (
    <div className="relative h-full min-h-0">
      <style>{`
        @keyframes bmBreathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.06) } }
        @keyframes bmSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes bmSpinR { from { transform: rotate(360deg) } to { transform: rotate(0deg) } }
        @keyframes bmRingP { 0% { transform: scale(.96); opacity: .8 } 100% { transform: scale(1.35); opacity: 0 } }
        @keyframes bmGlow { 0%,88%,100% { text-shadow: 0 0 6px rgba(0,240,255,.2) } 94% { text-shadow: 0 0 26px rgba(0,240,255,.75) } }
      `}</style>

      {/* center — BEAST CORE */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
        <div style={{ fontWeight: 200, fontSize: 56, letterSpacing: '.14em', color: '#e9f9ff', textShadow: '0 0 18px rgba(0,240,255,.25)' }}>BEAST CORE</div>
        <div style={{ ...mono(17, '.3em'), color: '#56788c', marginTop: 8 }}>THE AGENT&apos;S MEMORY · LIVE</div>
      </div>
      <div style={{ position: 'absolute', left: '50%', top: 320, width: 0, height: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', left: -110, top: -110, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle at 38% 32%, #bff4ff, #38c4e8 42%, #0a5d7d 78%, #063246)', boxShadow: '0 0 90px rgba(0,240,255,.5),0 0 220px rgba(0,240,255,.22)', animation: 'bmBreathe 4.5s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', left: -160, top: -160, width: 320, height: 320, borderRadius: '50%', border: '1px solid rgba(125,232,255,.28)', borderLeftColor: 'transparent', borderRightColor: 'transparent', animation: 'bmSpin 6s linear infinite' }} />
        <div style={{ position: 'absolute', left: -198, top: -198, width: 396, height: 396, borderRadius: '50%', border: '1px dashed rgba(125,232,255,.16)', animation: 'bmSpinR 11s linear infinite' }} />
        <div style={{ position: 'absolute', left: -110, top: -110, width: 220, height: 220, borderRadius: '50%', border: '1px solid rgba(158,240,255,.5)', animation: 'bmRingP 2.8s ease-out infinite' }} />
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 500, display: 'flex', justifyContent: 'center', gap: 46, pointerEvents: 'none' }}>
        {stats.map(bs => (
          <div key={bs.l} style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 100, fontSize: 60, color: '#aef0ff', textShadow: '0 0 18px rgba(0,240,255,.4)', animation: 'bmGlow 9s infinite', lineHeight: 1 }}>{bs.v}</div>
            <div style={{ ...mono(15, '.26em'), color: '#49677a', marginTop: 7 }}>{bs.l}</div>
          </div>
        ))}
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 590, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
        <span style={{ padding: '7px 18px', borderRadius: 999, border: '1px solid rgba(125,232,255,.3)', background: 'rgba(0,240,255,.05)', ...mono(18, '.12em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)' }}>
          LIVE .SOMA STATE · SAME GATEWAY AS CLASSIC /BRAIN
        </span>
      </div>

      {/* center-bottom — PRELOAD + MAP panels (v6 Beast Mind, real endpoints) */}
      <div style={{ position: 'absolute', left: 400, right: 420, top: 640, bottom: 0, display: 'flex', gap: 14 }}>
        <div style={{ ...panel, flex: 1 }}>
          <Scan />
          <div style={{ ...mono(18, '.26em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)' }}>PRELOAD · CONTINUATION STATE</div>
          {preloads?.current ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: preloads.current.stale ? '#ffc36b' : '#5df2b4', boxShadow: `0 0 10px ${preloads.current.stale ? 'rgba(255,195,107,.6)' : 'rgba(93,242,180,.6)'}`, flex: 'none' }} />
                <span style={{ ...mono(15, '.06em'), color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(preloads.current.path).split('/').pop()}</span>
                <span style={{ ...mono(14, '.16em'), color: preloads.current.stale ? '#ffc36b' : '#5df2b4', flexShrink: 0 }}>{preloads.current.stale ? 'STALE' : 'FRESH'}</span>
              </div>
              <div style={{ ...mono(14, '.14em'), color: '#56788c', marginTop: 8 }}>{(preloads.preloads || []).length} PRELOADS ON DISK</div>
            </div>
          ) : (
            <div style={{ ...mono(16, '.1em'), color: '#56788c', marginTop: 12 }}>{preloads ? 'NO PRELOAD — NEXT SESSION BOOTS FRESH' : 'SYNCING…'}</div>
          )}
        </div>
        <div style={{ ...panel, flex: 1 }}>
          <Scan />
          <div style={{ ...mono(18, '.26em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)' }}>MAPS · WORKFLOW TEMPLATES</div>
          {maps && maps.length ? maps.slice(0, 4).map((m: any, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#9ef0ff', boxShadow: '0 0 8px rgba(0,240,255,.5)', flex: 'none' }} />
              <span style={{ ...mono(15, '.08em'), color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name || m.id || String(m)}</span>
            </div>
          )) : (
            <div style={{ ...mono(16, '.1em'), color: '#56788c', marginTop: 12 }}>{maps ? 'NO MAPS IN THIS PROJECT — soma --map <name> CREATES ONE' : 'SYNCING…'}</div>
          )}
        </div>
      </div>

      {/* left — recent sessions + issues */}
      <div style={{ position: 'absolute', left: 0, top: 30, bottom: 0, width: 380, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ ...panel, flex: 1.4 }}>
          <Scan />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ ...mono(18, '.26em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)' }}>LIVE SESSIONS</span>
            <span style={{ flex: 1 }} />
            <span style={{ ...mono(15, '.2em'), color: '#56788c' }}>{sessions.length}</span>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 'calc(100% - 28px)', scrollbarWidth: 'thin' }}>
            {sessions.slice(0, 8).map(s => (
              <div key={s.slug} style={{ marginTop: 11, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(125,232,255,.14)', background: 'rgba(8,18,28,.5)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ ...mono(16), color: '#7de8ff' }}>{s.slug.slice(0, 10).toUpperCase()}</span>
                  <span style={{ flex: 1, fontWeight: 300, fontSize: 23, color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                  <span style={{ ...mono(15), color: s.state === 'working' ? '#5df2b4' : '#49677a', whiteSpace: 'nowrap' }}>{s.state.toUpperCase()}</span>
                </div>
                <div style={{ ...mono(15, '.12em'), color: '#56788c', marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.model?.id ? s.model.id.toUpperCase() : '—'}{s.contextUsage ? ` · CTX ${Math.round(s.contextUsage.percent)}%` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...panel, flex: .8, border: '1px solid rgba(255,195,107,.25)', background: 'linear-gradient(170deg,rgba(180,120,40,.07),rgba(8,18,28,.45) 55%)' }}>
          <div style={{ ...mono(18, '.26em'), color: '#ffc36b' }}>ISSUES</div>
          {issues.length ? issues.map(c => (
            <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 11 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.status === 'warn' ? '#ffc36b' : '#ff7d8a', boxShadow: '0 0 10px rgba(255,195,107,.5)', flex: 'none' }} />
              <span style={{ flex: 1, fontWeight: 300, fontSize: 22, color: '#c4d8e4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}{c.detail ? ` — ${c.detail}` : ''}</span>
            </div>
          )) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 11 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#5df2b4', boxShadow: '0 0 10px rgba(93,242,180,.6)' }} />
              <span style={{ fontWeight: 300, fontSize: 22, color: '#c4d8e4' }}>{doctor ? 'ALL DOCTOR CHECKS GREEN' : 'DOCTOR SYNCING…'}</span>
            </div>
          )}
        </div>
      </div>

      {/* right — .SOMA ANATOMY */}
      <div style={{ position: 'absolute', right: 0, top: 30, bottom: 0, width: 400, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', scrollbarWidth: 'thin' }}>
        <div style={{ ...mono(18, '.3em'), color: '#9ef0ff', textShadow: '0 0 10px rgba(0,240,255,.4)' }}>.SOMA ANATOMY</div>
        <div style={{ ...panel, padding: 14, flex: 'none' }}>
          <Scan />
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <span style={{ ...mono(16, '.24em'), color: '#7fa9bd' }}>MUSCLES · LEARNED PATTERNS</span>
            <span style={{ flex: 1 }} />
            <span style={{ ...mono(16), color: '#49677a' }}>{muscles.length}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 11 }}>
            {hotMuscles.map(m => {
              const h = heatOf(m.heat);
              return (
                <span key={m.name} title={`heat ${m.heat}`} style={{ padding: '5px 11px', borderRadius: 999, border: `1px solid ${h.k === 'cold' ? 'rgba(125,232,255,.14)' : h.c + '55'}`, color: h.c, ...mono(15, '.1em'), boxShadow: h.k === 'cold' ? 'none' : `0 0 14px -2px ${h.glow}`, whiteSpace: 'nowrap' }}>{m.name}</span>
              );
            })}
          </div>
        </div>
        <div style={{ ...panel, padding: 14, flex: 'none' }}>
          <Scan />
          <div style={{ ...mono(16, '.24em'), color: '#7fa9bd' }}>PROTOCOLS · BEHAVIORAL RULES</div>
          {hotProtocols.map(p => {
            const h = heatOf(p.heat);
            return (
              <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 9 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: h.c, boxShadow: h.k === 'cold' ? 'none' : `0 0 10px ${h.glow}`, flex: 'none' }} />
                <span style={{ flex: 1, fontWeight: 300, fontSize: 22, color: '#c4d8e4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                <span style={{ ...mono(14, '.16em'), color: h.c, whiteSpace: 'nowrap' }}>{h.k.toUpperCase()}</span>
              </div>
            );
          })}
        </div>
        {doctor && (
          <div style={{ ...panel, padding: 14, flex: 'none' }}>
            <Scan />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ ...mono(16, '.24em'), color: '#7fa9bd' }}>DOCTOR</span>
              <StatusChip label={doctor.healthy ? 'HEALTHY' : 'ATTENTION'} kind={doctor.healthy ? 'good' : 'warn'} />
            </div>
            {(doctor.checks || []).slice(0, 6).map(c => (
              <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 9 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.status === 'ok' ? '#5df2b4' : c.status === 'warn' ? '#ffc36b' : '#ff7d8a', flex: 'none' }} />
                <span style={{ flex: 1, fontWeight: 300, fontSize: 22, color: '#c4d8e4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                <span style={{ ...mono(15), color: '#56788c', whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.detail || ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** SESSIONS — live gateway sessions with model, state, context usage. Same /api/gw/sessions as classic. */
/* Match a spoken name to a session (Boss 2026-07-12 'full voice control over sessions').
   Normalizes both sides, then scores by token overlap so "tampons" hits "Tampons CA",
   "open bus shelters" hits "SEO · busshelters". Returns the best session or null. */
const normName = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
function matchSession(spoken: string, sessions: any[]): any | null {
  const q = normName(spoken); if (!q.length) return null;
  let best: any = null, score = 0;
  for (const s of sessions) {
    const hay = new Set([...normName(s.name || ''), ...normName(s.slug || '')]);
    let sc = 0; for (const w of q) if (hay.has(w)) sc += w.length; // longer token matches weigh more
    // substring fallback: "tampons" inside slug "tampons-ca"
    const joined = (s.slug || '' + s.name || '').toLowerCase();
    for (const w of q) if (w.length > 3 && joined.includes(w)) sc += 1;
    if (sc > score) { score = sc; best = s; }
  }
  return score > 0 ? best : null;
}

export function SessionsSurface({ navigate }: { navigate?: (v: any) => void } = {}) {
  const [sessions, setSessions] = useState<any[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const sessionsRef = useRef<any[]>([]);
  useEffect(() => { sessionsRef.current = sessions || []; }, [sessions]);

  useEffect(() => {
    let live = true;
    const load = () => fetch(`${GW}/sessions`).then(r => r.json())
      .then(d => live && setSessions(d.sessions || [])).catch(() => live && setSessions([]));
    load();
    const t = setInterval(load, 15_000);
    return () => { live = false; clearInterval(t); };
  }, []);

  /* Select a session: highlight it, scroll it into view, and route the assistant's next
     voice turn INTO it (Boss 2026-07-12 'I pick, I say open tampons, it opens in the assistant').
     __beastVoiceTarget + localStorage are the same channel ChatSurface uses for voice routing. */
  const select = (slug: string, jump = false) => {
    setSelected(slug);
    try { localStorage.setItem('beast:sessions-selected', slug); } catch {}
    (window as any).__beastVoiceTarget = slug;
    /* Boss 2026-07-12 'the session should open right into the assistant window': a click
       jumps into the chat surface with this session live; voice-select just arms + scrolls. */
    if (jump && navigate) { navigate({ surface: 'chat' }); return; }
    requestAnimationFrame(() => rowRefs.current[slug]?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  /* VOICE CONTROL (Boss 2026-07-12 'full control system — scroll, pick, open'):
     handle scroll + open/sort intents dispatched by the orb. Only claims a command it
     actually served (announces hit) so other surfaces can still win a miss. */
  useEffect(() => {
    const hit = (intent: string, label?: string) =>
      window.dispatchEvent(new CustomEvent('beast:voice-command-result', { detail: { intent, ok: true, label } }));
    const on = (e: Event) => {
      const { intent, arg } = (e as CustomEvent).detail || {};
      const el = scrollRef.current;
      if (intent === 'scroll' && el) {
        const dy = Math.round(el.clientHeight * 0.8);
        if (arg === 'down') el.scrollBy({ top: dy, behavior: 'smooth' });
        else if (arg === 'up') el.scrollBy({ top: -dy, behavior: 'smooth' });
        else if (arg === 'top') el.scrollTo({ top: 0, behavior: 'smooth' });
        else if (arg === 'bottom') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        else return;
        hit('scroll', arg);
      } else if (intent === 'open' || intent === 'sort') {
        const s = matchSession(arg || '', sessionsRef.current);
        if (s) { select(s.slug); hit(intent, s.name); }
      }
    };
    window.addEventListener('beast:voice-command', on);
    return () => window.removeEventListener('beast:voice-command', on);
  }, []);

  if (sessions === null)
    return <div className="pt-10 text-center" style={{ ...mono(18, '.3em'), color: 'var(--uif-t5)' }}>SYNCING SESSIONS…</div>;

  return (
    <div className="mx-auto max-w-4xl">
      <Glass className="p-5">
        <div className="flex items-center gap-3">
          <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t5)' }}>PARALLEL SESSIONS</div>
          <div style={{ ...mono(16), color: 'var(--uif-t6)' }}>{sessions.length}</div>
          {selected && <div style={{ ...mono(14, '.15em'), color: 'var(--uif-good)' }}>→ {sessions.find(s => s.slug === selected)?.name?.toUpperCase() || ''}</div>}
        </div>
        <div ref={scrollRef} className="mt-3 flex flex-col gap-2" style={{ maxHeight: '70vh', overflowY: 'auto', scrollbarWidth: 'thin' }}>
          {sessions.map((s) => (
            <div key={s.slug} ref={el => { rowRefs.current[s.slug] = el; }}
              onClick={() => select(s.slug, true)}
              className="flex items-center gap-4 py-1.5 cursor-pointer"
              style={{ borderBottom: '1px solid rgba(255,255,255,.04)',
                background: selected === s.slug ? 'rgba(93,242,180,.10)' : 'transparent',
                boxShadow: selected === s.slug ? 'inset 2px 0 0 var(--uif-good)' : 'none',
                borderRadius: 6, paddingLeft: 6, transition: 'background .2s' }}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                background: s.state === 'working' ? 'var(--uif-good)' : s.lastError ? 'var(--uif-bad)' : 'var(--uif-t6)',
                boxShadow: s.state === 'working' ? '0 0 8px rgba(93,242,180,.8)' : 'none',
                animation: s.state === 'working' ? 'uifGlow 2.2s infinite' : 'none',
              }} />
              <div className="w-44 flex-shrink-0 truncate" style={{ ...mono(18, '.06em'), color: 'var(--uif-t2)' }}>{s.name}</div>
              <div className="w-32 flex-shrink-0 truncate" style={{ ...mono(16), color: s.state === 'working' ? 'var(--uif-good)' : 'var(--uif-t5)' }}>{s.state.toUpperCase()}</div>
              <div className="flex-1 min-w-0 truncate" style={{ ...mono(16), color: 'var(--uif-t5)' }}>{s.model?.id || '—'}</div>
              <div className="w-20 text-right" style={{ ...mono(16), color: (s.contextUsage?.percent ?? 0) > 70 ? '#ffcf7d' : 'var(--uif-t4)' }}>
                {s.contextUsage ? `CTX ${Math.round(s.contextUsage.percent)}%` : '—'}
              </div>
            </div>
          ))}
        </div>
      </Glass>
    </div>
  );
}
